import { useCallback } from "react";
import { useQueryClient } from "react-query";

import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import { invalidateConnectionRequestsQueryCache } from "@/react/hooks/connectionRequests/connectionRequestCacheHelpers";
import {
  useConnectionRequestWebSocket,
  type ConnectionRequestWebSocketEvent,
} from "@/react/hooks/connectionRequests/useConnectionRequestWebSocket";
import { useCircuitBreaker } from "@/react/hooks/useCircuitBreaker";
import { useIsConnectEnabled } from "@/react/hooks/useIsConnectEnabled";
import { usePendingConnections } from "@/react/hooks/usePendingConnections";

export const useConnectButtonCount = () => {
  const { currentCommunityMember } = usePunditUserContext();
  const isConnectEnabled = useIsConnectEnabled();
  const isConnectBadgeCircuitBroken = useCircuitBreaker("connect_badge");
  const queryClient = useQueryClient();
  const isEnabled =
    !isConnectBadgeCircuitBroken &&
    isConnectEnabled &&
    !!currentCommunityMember;

  const { totalQueryAudience = 0, isLoading } = usePendingConnections({
    perPage: 1,
    options: {
      enabled: isEnabled,
    },
  });

  const handleWebSocketMessage = useCallback(
    (eventData: ConnectionRequestWebSocketEvent) => {
      if (eventData.event === "updatePendingRequestsCount") {
        invalidateConnectionRequestsQueryCache({ queryClient });
      } else if (eventData.event === "connectionStatusChanged") {
        invalidateConnectionRequestsQueryCache({
          queryClient,
          initiatorPublicUid: eventData.initiator_public_uid,
          recipientPublicUid: eventData.recipient_public_uid,
        });
      }
    },
    [queryClient],
  );

  useConnectionRequestWebSocket(
    isEnabled ? currentCommunityMember?.id : undefined,
    handleWebSocketMessage,
  );

  return {
    count: isEnabled ? totalQueryAudience : 0,
    isLoading: isEnabled && isLoading,
  };
};
